/**
 * Cart Subscribe Button
 * Conditionally displays "Subscribe & Save" button for cart items
 * that have selling plans available but don't currently have one assigned
 */

(function() {


  let cartDataCache = null;
  let updateTimer = null;

  async function fetchCartData() {
    return cartDataCache;
  }

  function itemHasSellingPlan(itemRow) {
    const optionsDiv = itemRow.querySelector('.cart-item__subscribe-options');
    const buttonWrapper = itemRow.querySelector('.cart-item__subscribe-button-wrapper');
    const subscriptionNameText = itemRow.querySelector('.cart-item__subscription-name');
    return optionsDiv?.dataset.hasSubscription === 'true' ||
      buttonWrapper?.dataset.hasSubscription === 'true' ||
      !!subscriptionNameText?.textContent.trim();
  }


  function updateSubscribeButtons() {
    const cartItemRows = document.querySelectorAll('.cart-item[data-has-selling-plans]');
    
    cartItemRows.forEach((itemRow) => {
      const hasSellingPlans = itemRow.getAttribute('data-has-selling-plans') === 'true';
      const variantId = parseInt(itemRow.getAttribute('data-variant-id'), 10);
      const buttonWrapper = itemRow.querySelector('.cart-item__subscribe-button-wrapper');
      const optionsDiv = itemRow.querySelector('.cart-item__subscribe-options');
      const subscriptionNameText = itemRow.querySelector('.cart-item__subscription-name');
      
      if (!buttonWrapper || !variantId) return;
      
      const hasSellingPlan = itemHasSellingPlan(itemRow);
      
      // Get total options count
      const totalOptions = parseInt(buttonWrapper?.querySelector('.cart-item__subscribe-button')?.dataset.totalOptions || optionsDiv?.dataset.totalOptions || '0', 10);
      
      // Show button only if product has selling plans AND doesn't have an active subscription
      if (hasSellingPlans && !hasSellingPlan) {
        buttonWrapper.style.display = '';
        
        if (optionsDiv) {
          optionsDiv.style.display = 'none';
        }
        
        if (subscriptionNameText) {
          subscriptionNameText.style.display = 'none';
        }
      } 
      else {
        buttonWrapper.style.display = 'none';
        // Show options only if there's an active subscription AND more than 1 option
        if (hasSellingPlan && optionsDiv && totalOptions > 1) {
          optionsDiv.style.display = 'flex';
          updateSelectForCurrentSubscription(itemRow, variantId);
          
          if (subscriptionNameText) {
            subscriptionNameText.style.display = 'none';
          }
        } else {
          if (optionsDiv) {
            optionsDiv.style.display = 'none';
          }
          
          if (subscriptionNameText && hasSellingPlan) {
            subscriptionNameText.style.display = '';
          }
        }
      }
    });
  }

  /**
   * Update select dropdown to show current subscription
   */
  function updateSelectForCurrentSubscription(itemRow, variantId) {
    if (!cartDataCache || !cartDataCache.items) return;

    const lineItemKey = itemRow.dataset.lineKey;
    const cartItem = lineItemKey
      ? cartDataCache.items.find(item => item.key === lineItemKey)
      : cartDataCache.items.find(item => item.variant_id === variantId);
    if (!cartItem || !cartItem.selling_plan_allocation) return;

    const select = itemRow.querySelector('.cart-item__subscribe-frequency-select');
    if (!select) return;

    const currentPlanId = cartItem.selling_plan_allocation.selling_plan.id;
    
    // Find the option that matches the current plan
    for (let option of select.options) {
      if (option.value !== 'unsubscribe') {
        const parts = option.value.split('-');
        const optionPlanId = parts[0];
        if (optionPlanId === String(currentPlanId)) {
          select.value = option.value;
          break;
        }
      }
    }
  }

  function scheduleUpdateSubscribeButtons(delay = 120) {
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(updateSubscribeButtons, delay);
  }

  function snapshotSubscriptionControls(itemRow) {
    const buttonWrapper = itemRow.querySelector('.cart-item__subscribe-button-wrapper');
    const optionsDiv = itemRow.querySelector('.cart-item__subscribe-options');
    const subscriptionNameText = itemRow.querySelector('.cart-item__subscription-name');
    const select = itemRow.querySelector('.cart-item__subscribe-frequency-select');

    return {
      buttonDisplay: buttonWrapper?.style.display,
      optionsDisplay: optionsDiv?.style.display,
      subscriptionNameDisplay: subscriptionNameText?.style.display,
      // A change event fires after the browser has already moved the select
      // to its new value. Keep the last confirmed value so a rejected write
      // can restore the server-backed choice instead of the optimistic one.
      selectedValue: select?.dataset.confirmedValue || select?.value,
      confirmedValue: select?.dataset.confirmedValue,
    };
  }

  function restoreSubscriptionControls(itemRow, snapshot) {
    if (!itemRow.isConnected) return;

    const buttonWrapper = itemRow.querySelector('.cart-item__subscribe-button-wrapper');
    const optionsDiv = itemRow.querySelector('.cart-item__subscribe-options');
    const subscriptionNameText = itemRow.querySelector('.cart-item__subscription-name');
    const select = itemRow.querySelector('.cart-item__subscribe-frequency-select');

    if (buttonWrapper) buttonWrapper.style.display = snapshot.buttonDisplay || '';
    if (optionsDiv) optionsDiv.style.display = snapshot.optionsDisplay || '';
    if (subscriptionNameText) subscriptionNameText.style.display = snapshot.subscriptionNameDisplay || '';
    if (select && snapshot.selectedValue) select.value = snapshot.selectedValue;
    if (select && snapshot.confirmedValue) select.dataset.confirmedValue = snapshot.confirmedValue;
  }

  function setSubscriptionControlsPending(itemRow, pending) {
    itemRow.querySelectorAll('.cart-item__subscribe-button, .cart-item__subscribe-frequency-select').forEach((control) => {
      control.disabled = pending;
      control.setAttribute('aria-busy', String(pending));
    });
  }

  async function submitSellingPlanChange(itemRow, lineItemKey, sellingPlanId, snapshot) {
    setSubscriptionControlsPending(itemRow, true);
    try {
      const updated = await updateCartItemSellingPlan(lineItemKey, sellingPlanId);

      if (!updated) {
        restoreSubscriptionControls(itemRow, snapshot);
        return false;
      }

      if (itemRow.isConnected) {
        const select = itemRow.querySelector('.cart-item__subscribe-frequency-select');
        if (select) select.dataset.confirmedValue = select.value;
      }
      return true;
    } catch (error) {
      console.error('Error applying subscription change:', error);
      restoreSubscriptionControls(itemRow, snapshot);
      return false;
    } finally {
      if (itemRow.isConnected) setSubscriptionControlsPending(itemRow, false);
    }
  }

  // subscribe button click
  async function handleSubscribeButtonClick(event) {
    const button = event.target.closest('.cart-item__subscribe-button');
    if (!button) return;

    const variantId = button.dataset.variantId;
    const lineItemKey = button.dataset.lineItemKey;
    const itemId = button.dataset.itemId;
    const totalOptions = parseInt(button.dataset.totalOptions || '0', 10);
    const firstPlanId = button.dataset.firstPlanId;

    const itemRow = button.closest('.cart-item');
    if (!itemRow) return;

    const buttonWrapper = itemRow.querySelector('.cart-item__subscribe-button-wrapper');
    const optionsDiv = itemRow.querySelector('.cart-item__subscribe-options');
    const select = optionsDiv?.querySelector('.cart-item__subscribe-frequency-select');
    const subscriptionNameText = itemRow.querySelector('.cart-item__subscription-name');
    const controlsSnapshot = snapshotSubscriptionControls(itemRow);

    // If only one option, apply it directly without showing dropdown
    if (totalOptions === 1 && firstPlanId) {
      await submitSellingPlanChange(itemRow, lineItemKey, firstPlanId, controlsSnapshot);
      return;
    }

    // If more than one option, show the dropdown
    if (totalOptions > 1) {
      // Show options and hide button
      if (buttonWrapper) {
        buttonWrapper.style.display = 'none';
      }

      if (optionsDiv) {
        optionsDiv.style.display = 'flex';
      }

      // Hide subscription name text when dropdown is shown
      if (subscriptionNameText) {
        subscriptionNameText.style.display = 'none';
      }

      // Apply the first/default selling plan option
      if (select && select.options.length > 0) {
        const firstOption = select.options[0];

        if (firstOption.value !== 'unsubscribe') {
          const selectedValue = firstOption.value;
          const parts = selectedValue.split('-');
          const sellingPlanId = parts[0];

          if (sellingPlanId) {
            select.value = selectedValue;
            await submitSellingPlanChange(itemRow, lineItemKey, sellingPlanId, controlsSnapshot);
          }
        }
      }
    }
  }

  /**
   * Update cart item with selling plan
   */
  async function updateCartItemSellingPlan(lineItemKey, sellingPlanId) {
    const coordinator = window.AifufandCartMutation;
    const mutation = coordinator?.reserve({ type: 'selling-plan-change' });
    if (coordinator && !mutation) {
      console.warn('Cart is already updating; subscription change was not sent.');
      return false;
    }

    let status;
    let retryAfter;
    try {
      const cartDrawerContainer = document.querySelector('cart-drawer');
      if (!cartDrawerContainer) {
        console.error('Cart drawer not found');
        return false;
      }

      if (mutation && !coordinator.start(mutation)) return false;

      // Shopify requires a 1-based line plus the current quantity when a
      // selling plan changes. Resolve it under the shared mutation lock so a
      // stale key cannot be paired with a newly rendered row.
      const cartResponse = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      status = cartResponse.status;
      retryAfter = cartResponse.headers.get('Retry-After');
      if (!cartResponse.ok) throw new Error(`Cart read failed: ${cartResponse.status}`);
      const cart = await cartResponse.json();
      cartDataCache = cart;
      const lineIndex = (cart.items || []).findIndex((item) => item.key === lineItemKey);
      if (lineIndex < 0) throw new Error('Cart item is no longer available');
      const cartItem = cart.items[lineIndex];
      const sections = typeof cartDrawerContainer.getSectionsToRender === 'function'
        ? cartDrawerContainer.getSectionsToRender().map((section) => section.id)
        : ['cart-drawer', 'cart-icon-bubble'];

      const body = JSON.stringify({
        line: lineIndex + 1,
        quantity: cartItem.quantity,
        selling_plan: sellingPlanId || '',
        sections,
        sections_url: window.location.pathname
      });

      const response = await fetch(`${window.routes?.cart_change_url || '/cart/change'}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body
      });
      status = response.status;
      retryAfter = response.headers.get('Retry-After');

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      const responseText = await response.text();
      let parsedState;
      try {
        parsedState = responseText ? JSON.parse(responseText) : {};
      } catch (error) {
        throw new Error(status === 429 ? 'Your cart is updating. Please wait a moment and try again.' : 'Invalid cart update response');
      }

      const responseStatus = Number(parsedState.status);
      if (Number.isFinite(responseStatus) && responseStatus >= 400) {
        status = responseStatus;
        throw new Error(parsedState.description || parsedState.message || `Cart update failed: ${responseStatus}`);
      }

      if (parsedState.errors) {
        const message = typeof parsedState.errors === 'string'
          ? parsedState.errors
          : parsedState.description || parsedState.message || 'Cart update failed';
        throw new Error(message);
      }

      if (!Array.isArray(parsedState.items)) {
        throw new Error('Invalid cart update response');
      }
      cartDataCache = parsedState;

      if (parsedState.sections && typeof cartDrawerContainer.renderContents === 'function') {
        const renderData = { sections: parsedState.sections, id: cartItem.variant_id };
        if (typeof parsedState.item_count === 'number') renderData.item_count = parsedState.item_count;
        cartDrawerContainer.renderContents(renderData);
      } else if (parsedState.sections?.['cart-drawer']) {
        const newCartDrawerHTML = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
        const newCartDrawer = newCartDrawerHTML.querySelector('#CartDrawer');
        if (newCartDrawer && typeof updateCartDrawer === 'function') updateCartDrawer(cartDrawerContainer, newCartDrawer, false);
      }

      setTimeout(() => {
        updateSubscribeButtons();
        attachEventListeners();
      }, 100);
      return true;
    } catch (error) {
      console.error('Error updating cart item selling plan:', error);
      return false;
    } finally {
      if (mutation) coordinator.release(mutation, { status, retryAfter });
    }
  }

  async function handleFrequencySelectChange(event) {
    const select = event.target;
    if (!select || !select.classList.contains('cart-item__subscribe-frequency-select')) return;

    const variantId = select.dataset.variantId;
    const lineItemKey = select.dataset.lineItemKey;
    const itemId = select.dataset.itemId;
    const selectedValue = select.value;
    
    const itemRow = select.closest('.cart-item');
    if (!itemRow) return;

    const buttonWrapper = itemRow.querySelector('.cart-item__subscribe-button-wrapper');
    const optionsDiv = itemRow.querySelector('.cart-item__subscribe-options');
    const subscriptionNameText = itemRow.querySelector('.cart-item__subscription-name');
    const controlsSnapshot = snapshotSubscriptionControls(itemRow);

    if (selectedValue === 'unsubscribe') {
      const updated = await submitSellingPlanChange(itemRow, lineItemKey, '', controlsSnapshot);
      if (!updated) return;

      if (buttonWrapper) {
        buttonWrapper.style.display = '';
      }

      if (optionsDiv) {
        optionsDiv.style.display = 'none';
      }

      if (subscriptionNameText) {
        subscriptionNameText.style.display = 'none';
      }
    } else {
      const parts = selectedValue.split('-');
      const sellingPlanId = parts[0]; // First part is the plan ID

      if (!sellingPlanId) {
        return;
      }

      const updated = await submitSellingPlanChange(itemRow, lineItemKey, sellingPlanId, controlsSnapshot);
      if (!updated) return;
      
      // dropdown visible -  subscription name hidden
      const totalOptions = parseInt(optionsDiv?.dataset.totalOptions || '0', 10);
      if (totalOptions > 1 && subscriptionNameText) {
        subscriptionNameText.style.display = 'none';
      }
    }
  }

  function runSubscribeButtonClick(event) {
    void handleSubscribeButtonClick(event).catch((error) => {
      console.error('Error handling subscribe button click:', error);
    });
  }

  function runFrequencySelectChange(event) {
    void handleFrequencySelectChange(event).catch((error) => {
      console.error('Error handling subscription frequency change:', error);
    });
  }

  function attachEventListeners() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const buttons = cartDrawer.querySelectorAll('.cart-item__subscribe-button');
    const selects = cartDrawer.querySelectorAll('.cart-item__subscribe-frequency-select');

    buttons.forEach(button => {
      button.removeEventListener('click', runSubscribeButtonClick);
      button.addEventListener('click', runSubscribeButtonClick);
    });

    selects.forEach(select => {
      select.removeEventListener('change', runFrequencySelectChange);
      select.addEventListener('change', runFrequencySelectChange);
      select.dataset.confirmedValue = select.value;
    });
  }

  function initEventListeners() {
    attachEventListeners();

    document.addEventListener('cart-drawer:render', () => {
      setTimeout(attachEventListeners, 100);
    });

    document.addEventListener('cart-drawer:update', () => {
      setTimeout(attachEventListeners, 100);
    });
  }

  function init() {
    scheduleUpdateSubscribeButtons(500);
    
    document.addEventListener('cart-drawer:update', () => {
      scheduleUpdateSubscribeButtons();
    });
    
    document.addEventListener('cart-drawer:open', () => {
      scheduleUpdateSubscribeButtons();
    });

    document.addEventListener('cart-drawer:render', () => {
      scheduleUpdateSubscribeButtons();
    });

    document.addEventListener('cart-drawer:connected', () => {
      scheduleUpdateSubscribeButtons(200);
    });
    
    if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      document.addEventListener('cart:update', () => {
        scheduleUpdateSubscribeButtons();
      });
    }

    initEventListeners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
